import { useTracking } from "react-tracking";
import { TrackingData } from "common/helpers/Analytics";
import React from "react";
import { ButtonText } from "common/components/ButtonText";

export type IButtonVariant =
  | "primary"
  | "secondary"
  | "tertiary"
  | "danger"
  | "roundDanger"
  | "roundWhite"
  | "action"
  | "link";

export interface IButtonProps<TrackingProps extends TrackingData> {
  variant?: IButtonVariant;
  size?: "sm" | "md" | "lg" | "xl" | "round";
  disabled?: boolean;
  /**
   * The name of the icon to be rendered before the text
   */
  leadingIcon?: string;
  /**
   * The name of the icon to be rendered after the text
   */
  trailingIcon?: string;
  /**
   * Style for the leading or trailing icon.
   * Defaults to filled
   */
  filled?: boolean;
  /**
   * Whether the button should be expand to the full width of its container
   */
  block?: boolean;
  children?: React.ReactNode;
  /**
   * The name of the action sent to analytics on click. Required so every Button is a conscious
   * analytics decision; pass `null` only for controls whose analytics are fired by the caller
   * from a handler (e.g. only on a real state change), not on every click.
   */
  analyticsAction: string | null;
  /**
   * Any additional data to be sent to analytics on click
   */
  analyticsData?: Partial<TrackingProps>;
  /**
   * Controls which side of the button is rounded. Makes grouping buttons easier.
   */
  rounded?: "right" | "left" | "both";
  /**
   * Overrides the variant's default text color. Must be a Tailwind text color class (e.g. "text-secondary-500").
   * Unlike className, this replaces rather than appends, so the override is reliable against the variant default.
   */
  color?: `text-${string}`;
}

export type CombinedProps<TrackingProps extends TrackingData> = IButtonProps<TrackingProps> &
  React.DetailedHTMLProps<React.ButtonHTMLAttributes<HTMLButtonElement>, HTMLButtonElement>;

const variants = {
  primary: "border border-primary-300 bg-primary-300 hover:bg-primary-400 focus:ring-primary-100 focus:outline-none",
  secondary: "border border-secondary-100 bg-white hover:bg-secondary-50 focus:ring-secondary-50 focus:outline-none",
  tertiary: "border border-transparent hover:bg-secondary-50 focus:ring-transparent focus:outline-none",
  danger: "border border-error-500 bg-error-500 hover:bg-error-700 text-white focus:ring-error-50 focus:outline-none",
  roundDanger: "bg-[#D85140] hover:bg-error-400 text-white focus:ring-error-100 focus:outline-none rounded-full",
  roundWhite: "bg-white hover:bg-white focus:ring-white focus:outline-none rounded-full",
  action:
    "border border-secondary-100 bg-secondary-100 hover:bg-secondary-50 focus:ring-secondary-50 focus:outline-none",
  link: "bg-transparent border-transparent underline rounded px-md py-xs focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-600 focus-visible:ring-offset-1",
};

const disabledStyles = {
  primary: "!bg-secondary-50 !border-secondary-50 text-secondary-300 cursor-not-allowed",
  secondary: "!bg-transparent !border-secondary-50 text-secondary-300 cursor-not-allowed",
  tertiary: "!bg-transparent !border-transparent text-secondary-300 cursor-not-allowed",
  danger: "!bg-secondary-50 !border-secondary-50 text-secondary-300 cursor-not-allowed",
  roundDanger: "!bg-error-50 text-error-300 cursor-not-allowed",
  roundWhite: "!bg-transparent text-gray-50 cursor-not-allowed",
  action: "!bg-transparent !border-secondary-50 text-secondary-300 cursor-not-allowed",
  link: "text-secondary-300 cursor-not-allowed",
};

const textStyles = {
  primary: "text-secondary-900",
  secondary: "text-secondary-900",
  tertiary: "text-secondary-900",
  danger: "text-secondary-900",
  roundDanger: "text-secondary-900",
  roundWhite: "text-secondary-900",
  action: "text-secondary-800",
  link: "text-blue-600 hover:text-blue-700",
};

const heights = {
  sm: "h-[24px]",
  md: "h-[32px]",
  lg: "h-[36px]",
  xl: "h-[40px]",
  round: "",
};

const roundedClasses = {
  right: "rounded-r-3xl ml-[-1px]",
  left: "rounded-l-3xl",
  both: "rounded-3xl",
};

/**
 *  Primary Button component for all user interactions.
 */
export const Button = <T extends TrackingData>(props: CombinedProps<T>): React.JSX.Element => {
  const {
    children,
    variant = "primary",
    size = "md",
    disabled,
    leadingIcon,
    trailingIcon,
    filled,
    block,
    className = "",
    analyticsAction,
    analyticsData = {} as Partial<T>,
    type = "button",
    color,
    ...rest
  } = props;
  const buttonTextSize = size === "sm" ? "sm" : "md";
  const { trackEvent } = useTracking<TrackingData>();

  const handleClick = (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    // Fire unless the caller explicitly opted out with null; an empty-string action still fires.
    if (analyticsAction !== null) {
      trackEvent({ action: analyticsAction, ...analyticsData });
    }
    if (rest.onClick) {
      rest.onClick(e);
    }
  };
  const isLink = variant === "link";
  const ownClassName = `flex items-center justify-center
  ${!isLink ? `font-medium px-md focus:ring-[3px] ${heights[size]} ${roundedClasses[props.rounded || "both"]}` : ""}
  ${variants[variant]} ${block ? "w-full" : ""}
  ${variant.startsWith("round") ? "py-md" : ""} ${disabled ? disabledStyles[variant] : color || textStyles[variant]}`;

  return (
    <button {...rest} type={type} className={`${ownClassName} ${className}`} disabled={disabled} onClick={handleClick}>
      {leadingIcon && (
        <span
          className={`material-symbols-outlined ${filled ? "filled" : ""} font-semibold ${children ? "mr-xs" : ""}  ${
            size === "round" ? "text-[18px]" : "text-[16px]"
          }`}>
          {leadingIcon}
        </span>
      )}

      <ButtonText size={buttonTextSize}>{children}</ButtonText>

      {trailingIcon && (
        <span className={`material-symbols-outlined ${filled ? "filled" : ""} font-semibold ml-xs text-[16px]`}>
          {trailingIcon}
        </span>
      )}
    </button>
  );
};
